import React from 'react';
import { Box, HStack, Text, Badge, Icon, Spinner } from '@chakra-ui/react';
import { FaCircle } from 'react-icons/fa';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api';
import { useTranslation } from 'react-i18next';
import { usePageVisibility } from '../hooks/usePageVisibility';

/** Same payload as RecentActivityFeed online card — shared React Query cache key. */
interface OnlineUsersSummary {
  totalOnline: number;
  teachersOnline: number;
  studentsOnline: number;
}

/**
 * Online Status Widget - Shows real-time online user counts
 * Only visible to ADMIN users
 * Displays in a small box at corner of screen
 * Pauses polling when tab is hidden to save resources
 */
export default function OnlineStatusWidget(): JSX.Element | null {
  const { t } = useTranslation();
  const isVisible = usePageVisibility();

  const { data, isLoading, error, dataUpdatedAt } = useQuery<OnlineUsersSummary>({
    queryKey: ['online-users'],
    queryFn: () => apiGet<OnlineUsersSummary>('/api/activity/online-users'),
    refetchInterval: isVisible ? 30000 : false,
    enabled: isVisible,
    retry: false,
  });

  // Don't show widget if user is not authorized (403 error)
  if (error) {
    return null;
  }

  if (isLoading) {
    return (
      <Box
        position="fixed"
        bottom={4}
        right={4}
        bg="white"
        borderRadius="md"
        boxShadow="lg"
        p={3}
        zIndex={1000}
        minW="200px"
      >
        <HStack spacing={2}>
          <Spinner size="sm" />
          <Text fontSize="sm" color="gray.600">
            {t('onlineStatus.loading')}
          </Text>
        </HStack>
      </Box>
    );
  }

  if (!data) return null;

  const totalOnline = data.totalOnline ?? 0;
  const teachersOnline = data.teachersOnline ?? 0;
  const studentsOnline = data.studentsOnline ?? 0;

  return (
    <Box
      position="fixed"
      bottom={4}
      right={4}
      bg="white"
      borderRadius="md"
      boxShadow="lg"
      p={4}
      zIndex={1000}
      minW="220px"
      borderWidth="1px"
      borderColor="gray.200"
    >
      <HStack spacing={2} mb={3}>
        <Icon as={FaCircle} color="green.500" boxSize={2} />
        <Text fontSize="sm" fontWeight="bold" color="gray.700">
          {t('onlineStatus.title')}
        </Text>
      </HStack>

      <Box>
        <HStack justify="space-between" mb={2}>
          <Text fontSize="sm" color="gray.600">
            {t('onlineStatus.total')}
          </Text>
          <Badge colorScheme="green" fontSize="sm" px={2}>
            {totalOnline}
          </Badge>
        </HStack>

        <HStack justify="space-between" mb={2}>
          <Text fontSize="sm" color="gray.600">
            {t('onlineStatus.teachers')}
          </Text>
          <Badge colorScheme="purple" fontSize="sm" px={2}>
            {teachersOnline}
          </Badge>
        </HStack>

        <HStack justify="space-between">
          <Text fontSize="sm" color="gray.600">
            {t('onlineStatus.students')}
          </Text>
          <Badge colorScheme="blue" fontSize="sm" px={2}>
            {studentsOnline}
          </Badge>
        </HStack>
      </Box>

      <Text fontSize="xs" color="gray.400" mt={3} textAlign="center">
        {t('onlineStatus.lastUpdated')}: {new Date(dataUpdatedAt).toLocaleTimeString()}
      </Text>
    </Box>
  );
}
